import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  Inject,
  DOCUMENT,
  OnChanges
} from '@angular/core';
import {
  OBTRailJourneyFare,
  RailEnterpriseSearchInterface,
  OBTRailJourneyOption,
  OBTRailFareTypes,
  RailFareTypes,
  RailDirection,
  ServiceProvider,
  RailBasketItem,
  ICheapestPrice,
  IRailTicketType,
  RailResultsObject,
  SingleOrReturn,
  journeyTime,
  checkForBus,
  convertChangesToNumber,
  trackRowFunction
} from '@sabstravtech/obtservices/base';
import {
  ServiceType,
  EnterpriseSearchService,
  WithSubscriptionComponent,
  ModalOpenerService,
  EnterpriseBasketService,
  RailSearchJourneyType,
  RailJourneyFare,
  UserService,
  RailClass,
  RailFareProcessingService,
  RailResultsStateService
} from '@sabstravtech/obtservices/angular';
import moment from 'moment';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { faCalendarExclamation } from '@fortawesome/pro-regular-svg-icons';

import { resultIcons } from '../../../../core/enum/result-icons.enum';
import { LightningModalTypes } from '../../../../core/classes/modal-types.enum';
import { Helpers } from '../../../../core/classes/helpers';
import { SabsDatePipe } from '../../../../startup/pipes/sabs-date.pipe';

@Component({
  selector:
    'app-rail-results-block[sourceId][journeys][from][to][fareTypes][expandedTickets][direction]',
  templateUrl: './rail-results-block.component.html',
  styleUrls: ['./rail-results-block.component.scss'],
  standalone: false
})
export class RailResultsBlockComponent
  extends WithSubscriptionComponent
  implements OnInit, OnChanges
{
  @Input() sourceId: number;
  @Input() journeys: OBTRailJourneyOption[];
  @Input() outboundJourneys: OBTRailJourneyOption[];
  @Input() from: string;
  @Input() to: string;
  @Input() date: string;
  @Input() fareTypes: Record<OBTRailFareTypes, string[]>;
  @Input() expandedTickets: Record<OBTRailFareTypes, Record<string, boolean>>;
  @Input() direction: RailDirection;
  @Input() isExchange = false;
  @Input() selectedJourneys: boolean[];
  @Input() exchangeSelectHandler?: (
    direction: RailDirection,
    journey: OBTRailJourneyOption,
    fare: OBTRailJourneyFare,
    sourceId: string,
    section: OBTRailFareTypes
  ) => void;
  @Input() hidePagination = false;

  @Output() earlier = new EventEmitter<boolean>();
  @Output() later = new EventEmitter<void>();
  @Output() prevDay = new EventEmitter<void>();
  @Output() nextDay = new EventEmitter<void>();
  @Output() exchangedItemDetail: EventEmitter<RailBasketItem> = new EventEmitter();

  readonly eTicketIcon = 'https://images.sabscorp.com/images/sabs2/icons/eticket.jpg';

  resultItemType = resultIcons;
  ticketTypes: RailFareTypes[];
  RailFareTypes: typeof RailFareTypes = RailFareTypes;
  RailDirection: typeof RailDirection = RailDirection;
  ServiceProvider: typeof ServiceProvider = ServiceProvider;
  searchParams: RailEnterpriseSearchInterface;
  canOverride = false;
  sourceName = '';
  isDateDifferent = false;
  RailSearchJourneyType: typeof RailSearchJourneyType = RailSearchJourneyType;
  returnValidFares = {};

  newResultsObject: RailResultsObject = {
    standardSingles: {},
    firstClassSingles: {},
    standardReturns: {},
    firstClassReturns: {}
  };

  busyTimes: { [journeyHash: string]: boolean } = {};
  datesAfterInitial: boolean[] = [];
  displayStandardSingles = true;
  displayFirstClassSingles = true;
  displayStandardReturns = true;
  displayFirstClassReturns = true;
  lowestCarbonValue: number | null = null;
  cheapestNotSplits: ICheapestPrice = { amount: null, currencyCode: null };
  ticketSavings: { [ticketHash: string]: string } = {};
  faCalendarExclamation = faCalendarExclamation;
  unavailableJourneysDueToTime: { [journeyHash: string]: boolean } = {};

  // Expose utility functions to template
  journeyTime = journeyTime;
  checkForBus = checkForBus;
  convertChangesToNumber = convertChangesToNumber;
  trackRowFunction = trackRowFunction;

  constructor(
    private searchService: EnterpriseSearchService,
    private modalService: ModalOpenerService,
    private basketService: EnterpriseBasketService,
    private userService: UserService,
    private sabsDatePipe: SabsDatePipe,
    private fareProcessingService: RailFareProcessingService,
    private resultsStateService: RailResultsStateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
    this.searchParams = this.searchService.searches[ServiceType.Rail];
    this.getTicketTypes = this.getTicketTypes.bind(this);
    this.ticketTypes = this.searchParams.ticketTypes;
  }

  ngOnInit(): void {
    this.canOverride = this.userService.canOverride();
    this.sourceName = this.userService.findService(this.sourceId.toString())?.name;
    this.isDateDifferent = this.checkDepartDateTime();
    this.initializeResults();
    this.updateColumnDisplay();
    this.cheapestNotSplits = this.searchParams.findCheapestTicket(true);
    this.updateTicketSavings();

    this.subscribe(this.searchParams.selectedTicketSelected, () => {
      this.journeys.forEach((journey) => {
        this.unavailableJourneysDueToTime[journey.journeyHash] =
          this.checkIfRowIsUnavailable(journey);
      });
    });
  }

  ngOnChanges(changes: any): void {
    if (changes.journeys) {
      this.newResultsObject = this.resultsStateService.updateNewResultsItem(
        this.journeys,
        this.newResultsObject
      );
      if (this.direction === RailDirection.Inbound && this.searchParams.selectedTicket) {
        this.newResultsObject = this.resultsStateService.updateInboundReturns(
          this.journeys,
          this.newResultsObject
        );
      }
      this.updateColumnDisplay();
      this.cheapestNotSplits = this.searchParams.findCheapestTicket(true);
      this.updateTicketSavings();
      this.journeys.forEach((journey) => {
        this.unavailableJourneysDueToTime[journey.journeyHash] =
          this.checkIfRowIsUnavailable(journey);
      });
    }
  }

  private initializeResults(): void {
    const { resultsObject, lowestCarbonValue, busyTimes, datesAfterInitial } =
      this.resultsStateService.setupNewResultsItem(this.journeys);

    this.newResultsObject = resultsObject;
    this.lowestCarbonValue = lowestCarbonValue;
    this.busyTimes = busyTimes;
    this.datesAfterInitial = datesAfterInitial;
  }

  private updateColumnDisplay(): void {
    const flags = this.resultsStateService.setColumnsToDisplay(
      this.journeys,
      this.newResultsObject
    );
    this.displayStandardSingles = flags.displayStandardSingles;
    this.displayFirstClassSingles = flags.displayFirstClassSingles;
    this.displayStandardReturns = flags.displayStandardReturns;
    this.displayFirstClassReturns = flags.displayFirstClassReturns;
  }

  private updateTicketSavings(): void {
    this.ticketSavings = this.fareProcessingService.calculateTicketSavings(this.journeys);
  }

  // Template-bound method for field extraction
  fieldFromObject(
    journey: OBTRailJourneyOption,
    isExpanded: boolean,
    fareTypes: Record<OBTRailFareTypes, string[]>,
    journeyType: OBTRailFareTypes,
    ticketName: string,
    index: number
  ): any {
    if (
      journeyType === RailFareTypes.openReturnJourneyFares &&
      this.direction === RailDirection.Inbound
    ) {
      return null;
    }

    if (isExpanded) {
      const expandedResult = Helpers.fieldFromObjectArray<OBTRailJourneyFare>(journey, [
        journeyType,
        ticketName,
        index
      ]);
      if (expandedResult?.selectable && (!expandedResult?.unavailable || this.canOverride)) {
        this.returnValidFares[journeyType] = fareTypes[journeyType];
      } else if (!expandedResult?.selectable && expandedResult?.unavailable) {
        this.returnValidFares[journeyType] = [];
      }

      if (expandedResult?.removeResult) {
        this.returnValidFares[journeyType] = [];
        return null;
      }

      return expandedResult;
    }

    const result = Helpers.fieldFromObjectArray<OBTRailJourneyFare[]>(journey, [
      journeyType,
      ticketName
    ])?.reduce(
      (
        lowest: OBTRailJourneyFare | null,
        ticket: OBTRailJourneyFare
      ): OBTRailJourneyFare | null => {
        if (!ticket) {
          return lowest;
        }
        if (lowest && lowest.price < ticket.price) {
          return lowest;
        } else {
          return ticket || null;
        }
      },
      null
    );

    if (result?.removeResult) {
      this.returnValidFares[journeyType] = [];
      return null;
    }

    if (result?.selectable && (!result?.unavailable || this.canOverride)) {
      this.returnValidFares[journeyType] = fareTypes[journeyType];
    } else if (!result?.selectable && result?.unavailable) {
      this.returnValidFares[journeyType] = [];
    }
    return result;
  }

  async onSelectTicket(
    railJourneyOption: OBTRailJourneyOption,
    railFare: OBTRailJourneyFare,
    section: OBTRailFareTypes
  ): Promise<void> {
    if (this.isExchange) {
      if (this.exchangeSelectHandler) {
        this.exchangeSelectHandler(
          this.direction,
          railJourneyOption,
          railFare,
          this.sourceId.toString(),
          section
        );
      } else {
        const isOneWaySlection = this.selectedJourneys?.includes(false);
        const basketItem = await this.searchParams.selectTicketForExchange(
          this.direction,
          railJourneyOption,
          railFare,
          this.sourceId.toString(),
          isOneWaySlection ? RailFareTypes.singleJourneyFares : section
        );

        this.exchangedItemDetail.emit(basketItem);
      }
    } else {
      if (railFare.selectable) {
        this.searchParams.selectTicket(
          this.direction,
          railJourneyOption,
          railFare,
          this.sourceId.toString(),
          section
        );

        if (
          this.searchParams.chosenSearchType === RailSearchJourneyType.SingleJourney ||
          (this.searchParams.chosenSearchType === RailSearchJourneyType.ReturnJourney &&
            this.direction === RailDirection.Inbound)
        ) {
          this.basketService.closeMenu();
          if (this.searchService.searches[ServiceType.Flight].railFromFlightSearchPerformed) {
            this.searchService.search_objects[ServiceType.Flight].chosen = false;
          }
        }
      }
    }
  }

  async openRailTicketDetailsDialog(
    journey: OBTRailJourneyOption,
    ticket: OBTRailJourneyFare
  ): Promise<void> {
    if (journey && ticket) {
      const terms = ticket.terms;
      const identifiers = ticket.identifiers;
      this.searchParams.openRailTicketDetailsDialog(
        journey,
        ticket,
        terms,
        identifiers,
        this.sourceId.toString()
      );
    }
  }

  getTicketTypes(type: OBTRailFareTypes): IRailTicketType[] {
    if (!this.journeys?.length) {
      return [];
    }

    if (type === RailFareTypes.returnJourneyFares && this.direction === RailDirection.Inbound) {
      const outboundFares = this.fareProcessingService.sortTickets(this.outboundJourneys, type);
      const inboundFares = this.fareProcessingService.sortTickets(this.journeys, type);

      return outboundFares
        .map((x) => x.key)
        .reduce((curr, next) => {
          const inboundFare = inboundFares.find((x) => x.key === next);
          curr.push(inboundFare);
          return curr;
        }, []);
    }

    if (type !== RailFareTypes.returnJourneyFares) {
      this.journeys.forEach((j) => {
        for (const p in j[type]) {
          if (j[type][p].filter((o) => o === null).length === j[type][p].length) {
            delete j[type][p];
          }
        }
      });
    }

    return this.fareProcessingService.sortTickets(this.journeys, type);
  }

  isEticketAvailable(
    isExpanded: boolean,
    fareTypes: Record<OBTRailFareTypes, string[]>,
    journeyType: OBTRailFareTypes,
    ticketName: string,
    index: number
  ): boolean {
    let eticket = false;
    this.journeys.forEach((journey: OBTRailJourneyOption) => {
      const ticket: RailJourneyFare = this.fieldFromObject(
        journey,
        isExpanded,
        fareTypes,
        journeyType,
        ticketName,
        index
      );
      if (ticket) {
        eticket = !!ticket.eTicketAvailable;
      }
    });
    return eticket;
  }

  isLimitedRow(
    isExpanded: boolean,
    fareTypes: Record<OBTRailFareTypes, string[]>,
    journeyType: OBTRailFareTypes,
    ticketName: string,
    index: number
  ): boolean {
    let limited = false;
    this.journeys.forEach((journey: OBTRailJourneyOption) => {
      const ticket: RailJourneyFare = this.fieldFromObject(
        journey,
        isExpanded,
        fareTypes,
        journeyType,
        ticketName,
        index
      );
      if (ticket) {
        limited = !!(ticket.availableSeats && ticket.availableSeats < 9);
      }
    });
    return limited;
  }

  isGroupFare(ticket: RailJourneyFare): boolean {
    let found = false;
    if (ticket) {
      found = this.fareProcessingService.isGroupFare(ticket);
    }
    return found;
  }

  isTrainlineSplitFare(
    isExpanded: boolean,
    fareTypes: Record<OBTRailFareTypes, string[]>,
    journeyType: OBTRailFareTypes,
    ticketName: string,
    index: number
  ): boolean {
    return this.journeys.some((journey: OBTRailJourneyOption) => {
      const ticket: RailJourneyFare = this.fieldFromObject(
        journey,
        isExpanded,
        fareTypes,
        journeyType,
        ticketName,
        index
      );
      return this.fareProcessingService.isTrainlineSplitFare(ticket);
    });
  }

  showSplitTooltip(
    isExpanded: boolean,
    fareTypes: Record<OBTRailFareTypes, string[]>,
    journeyType: OBTRailFareTypes,
    ticketName: string,
    index: number
  ): boolean {
    return this.journeys.some((journey: OBTRailJourneyOption) => {
      const ticket: RailJourneyFare = this.fieldFromObject(
        journey,
        isExpanded,
        fareTypes,
        journeyType,
        ticketName,
        index
      );
      return this.fareProcessingService.shouldShowSplitTooltip(ticket);
    });
  }

  checkDepartDateTime(): boolean {
    if (this.journeys[0]?.departDateTime) {
      const formattedDate = this.sabsDatePipe.transform(this.journeys[0].departDateTime, 2, null);
      return this.date !== formattedDate;
    }
    return false;
  }

  getHiddenText(): string {
    if (
      this.searchParams.chosenSearchType === RailSearchJourneyType.ReturnJourney &&
      this.direction === RailDirection.Inbound &&
      this.searchParams.selectedTicket
    ) {
      if (this.searchParams.selectedTicket.fare?.singleOrReturn === SingleOrReturn.Single) {
        return 'Inbound dual single options can now be selected';
      } else if (this.searchParams.selectedTicket.fare?.singleOrReturn === SingleOrReturn.Return) {
        if (this.searchParams.selectedTicket.fare?.class === RailClass.First) {
          return 'Inbound 1st class return options can now be selected';
        } else if (this.searchParams.selectedTicket.fare?.class === RailClass.Standard) {
          return 'Inbound standard return options can now be selected';
        } else {
          return 'Inbound return options can now be selected';
        }
      } else {
        return 'Inbound options can now be selected';
      }
    } else {
      return '';
    }
  }

  getId(ticketType: string, fareTypeIndex: number, index: number): string {
    const directionPrefix = this.direction === RailDirection.Outbound ? 'outbound' : 'inbound';
    const capitalizedTicketType = ticketType.charAt(0).toUpperCase() + ticketType.slice(1);
    return `${directionPrefix}${capitalizedTicketType}${fareTypeIndex + 1}${index + 1}`;
  }

  openDetailsModal(journey: OBTRailJourneyOption): void {
    this.modalService.open(
      LightningModalTypes.ModalRailJourneyDetailsComponent,
      {},
      { result: journey }
    );
  }

  async openTicketsOfTheSameType(
    journey: OBTRailJourneyOption,
    railFare: OBTRailJourneyFare
  ): Promise<void> {
    const ticketArray = this.fareProcessingService.getAllTicketsFromJourney(
      journey,
      railFare.class,
      railFare.singleOrReturn
    );

    // Calculate correct price baseline for modal split savings
    let priceSoFar = 0;

    const hasDualSingleFares = this.journeys?.some(
      (j) => j.dualSingleJourneyFares && Object.keys(j.dualSingleJourneyFares).length > 0
    );

    if (hasDualSingleFares) {
      const cheapestNotSplit = Number(this.cheapestNotSplits.amount);
      if (cheapestNotSplit) {
        priceSoFar = cheapestNotSplit;
      }
    } else {
      const cheapestNotSplit = Number(this.cheapestNotSplits.amount);
      const selectedPrice = this.searchParams.selectedTicket
        ? Number(this.searchParams.selectedTicket?.fare?.price)
        : 0;
      priceSoFar = cheapestNotSplit - selectedPrice;
    }

    await this.modalService
      .open(
        LightningModalTypes.ModalRailTickets,
        { centered: true },
        {
          tickets: ticketArray,
          sourceName: this.sourceName,
          journey: journey,
          priceSoFar: priceSoFar,
          searchType: this.searchParams.chosenSearchType,
          previouslySelectedTicket: this.searchParams.selectedTicket
        }
      )
      .then((result: OBTRailJourneyFare) => {
        if (result) {
          const ticketType = this.fareProcessingService.getTicketType(journey, result);
          if (result?.class === RailClass.Standard) {
            if (result?.singleOrReturn === SingleOrReturn.Single) {
              this.newResultsObject.standardSingles[journey.journeyHash] = result;
              this.onSelectTicket(journey, result, ticketType);
            } else if (result?.singleOrReturn === SingleOrReturn.Return) {
              this.newResultsObject.standardReturns[journey.journeyHash] = result;
              this.onSelectTicket(journey, result, ticketType);
            }
          } else if (result?.class === RailClass.First) {
            if (result?.singleOrReturn === SingleOrReturn.Single) {
              this.newResultsObject.firstClassSingles[journey.journeyHash] = result;
              this.onSelectTicket(journey, result, ticketType);
            } else if (result?.singleOrReturn === SingleOrReturn.Return) {
              this.newResultsObject.firstClassReturns[journey.journeyHash] = result;
              this.onSelectTicket(journey, result, ticketType);
            }
          }
        }
      });
  }

  splitSavings(ticket: OBTRailJourneyFare): string {
    return this.ticketSavings[ticket.fareHash] || '';
  }

  getSplitTicketTooltip(ticket: OBTRailJourneyFare): string {
    return this.fareProcessingService.getSplitTicketTooltip(ticket, this.journeys);
  }

  getTicketType(journey: OBTRailJourneyOption, railFare: OBTRailJourneyFare): RailFareTypes {
    return this.fareProcessingService.getTicketType(journey, railFare);
  }

  checkTooltipFocus(buttonTt: NgbTooltip): void {
    setTimeout(() => {
      if (
        !this.document.activeElement.classList.contains('view_conditions') &&
        !this.document.activeElement.classList.contains('rail-price')
      ) {
        buttonTt.close();
      }
    }, 0);
  }

  checkIfRowIsUnavailable(journey: OBTRailJourneyOption): boolean {
    if (this.direction === RailDirection.Inbound) {
      if (this.searchParams.selectedTicket?.journey) {
        return moment(journey.departDateTime).isSameOrBefore(
          moment(this.searchParams.selectedTicket.journey.arriveDateTime)
        );
      } else {
        return false;
      }
    } else {
      return false;
    }
  }
}
